import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  FormSearch,
  FormSelect,
  FormText,
  InventoryImportConfirmCloseModal,
  PaginationNavButton,
  PaginationPageButton,
  PrimaryButton,
} from '..'
import type {
  InventoryImportProduct,
  ProductErrors,
} from '../../modules/Inventory/InventoryImportReview'
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'

type OptionLike = { id?: any; name?: string; value?: any; description?: string }

export const InventoryImportProductsList: FC<{
  orderIndex: number
  products: InventoryImportProduct[]
  errors: ProductErrors[]
  productOptions: any[]
  productsMap: Record<string, { Name: string; Count?: number }>
  pieceCategories: OptionLike[]
  onChangeProduct: (
    productIndex: number,
    patch: Partial<InventoryImportProduct>
  ) => void
  onAddProduct?: () => void
  onRemoveProduct?: (productIndex: number) => void
  priorUsage?: Record<string, number>
}> = ({
  orderIndex,
  products,
  errors,
  productOptions,
  productsMap,
  pieceCategories,
  onChangeProduct,
  onAddProduct,
  onRemoveProduct,
  priorUsage,
}) => {
  const pageSizes = [5, 10, 20]
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [prodQuery, setProdQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null) // -1 = nuevo
  const [editorDraft, setEditorDraft] = useState<InventoryImportProduct | null>(
    null
  )
  const [isDirty, setIsDirty] = useState(false)
  const [showConfirmClose, setShowConfirmClose] = useState(false)
  const [pendingScrollIndex, setPendingScrollIndex] = useState<number | null>(
    null
  )
  const [highlightIndex, setHighlightIndex] = useState<number | null>(null)
  // Add search states for category and product
  const [categorySearchValue, setCategorySearchValue] = useState('')
  const [productSearchValue, setProductSearchValue] = useState('')
  const cardRefs = useRef<Record<number, HTMLButtonElement | null>>({})

  // Sync search values when editor opens
  useEffect(() => {
    if (selectedIndex != null) {
      const row =
        editorDraft ||
        (selectedIndex >= 0
          ? products[selectedIndex]
          : ({} as InventoryImportProduct))
      const code = (row.productCode || '').trim()
      const currentProduct = code ? productsMap[code] : undefined
      const cat =
        pieceCategories.find(
          (c) => c.id?.toString() === row.categoryId?.toString()
        )?.name || ''
      const prodDisplay = currentProduct
        ? `${row.productCode || ''} · ${currentProduct.Name}`
        : row.productCode || ''
      setCategorySearchValue(cat)
      setProductSearchValue(prodDisplay)
    }
  }, [selectedIndex, editorDraft, products, productsMap, pieceCategories])

  // Bloquear scroll de fondo cuando el modal (agregar/editar producto) está abierto
  useEffect(() => {
    if (selectedIndex != null) {
      const prevOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prevOverflow || ''
      }
    }
  }, [selectedIndex])

  const norm = (s: any) => (s || '').toString().toLowerCase().trim()
  const filteredIndicesAll = useMemo(() => {
    const q = norm(prodQuery)
    if (!q) return products.map((_, i) => i)
    return products
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => {
        const code = norm(p.productCode)
        const cat = norm(
          pieceCategories.find(
            (c) => c.id?.toString() === p.categoryId?.toString()
          )?.name || ''
        )
        const name = code ? norm(productsMap[code]?.Name) : ''
        return code.includes(q) || cat.includes(q) || name.includes(q)
      })
      .map(({ i }) => i)
  }, [products, pieceCategories, productsMap, prodQuery])

  const total = filteredIndicesAll.length
  const totalPages = Math.max(1, Math.ceil(total / size))
  const safePage = Math.min(page, totalPages - 1)
  const start = safePage * size
  const end = Math.min(start + size, total)
  const displayIndices = filteredIndicesAll.slice(start, end)

  // Limpiar selección si sale de la ventana filtrada (no limpiar para -1: nuevo)
  useEffect(() => {
    if (
      selectedIndex != null &&
      selectedIndex >= 0 &&
      !displayIndices.includes(selectedIndex)
    ) {
      setSelectedIndex(null)
    }
  }, [displayIndices, selectedIndex])

  // Preparar draft al seleccionar
  useEffect(() => {
    if (selectedIndex == null) {
      setEditorDraft(null)
      setIsDirty(false)
      return
    }
    if (selectedIndex === -1) {
      setEditorDraft({
        productCode: '',
        productName: '',
        quantity: 0,
        length: undefined,
        width: undefined,
        height: undefined,
        weight: undefined,
        declaredValue: undefined,
        categoryId: undefined,
      })
      setIsDirty(false)
      return
    }
    const original = products[selectedIndex]
    if (original) {
      setEditorDraft({ ...original })
      setIsDirty(false)
    }
  }, [selectedIndex, products])

  const updateDraft = (patch: Partial<InventoryImportProduct>) => {
    setEditorDraft((prev) => {
      const next = { ...(prev as InventoryImportProduct), ...patch }
      setIsDirty(true)
      return next
    })
  }

  const closeEditor = () => {
    if (isDirty) setShowConfirmClose(true)
    else setSelectedIndex(null)
  }

  const confirmClose = (accept: boolean) => {
    if (accept) {
      setShowConfirmClose(false)
      setSelectedIndex(null)
      setEditorDraft(null)
      setIsDirty(false)
    } else setShowConfirmClose(false)
  }

  const saveChanges = () => {
    if (selectedIndex == null || !editorDraft) return
    const isNew = selectedIndex === -1
    const rowErrors = validateProduct(
      editorDraft,
      products,
      productsMap,
      isNew ? products.length : selectedIndex
    )
    if (Object.keys(rowErrors).length > 0) return

    if (isNew) {
      const newIndex = products.length
      onAddProduct && onAddProduct()
      onChangeProduct(newIndex, editorDraft)
      const targetPage = Math.floor(newIndex / size)
      if (targetPage !== safePage) setPage(targetPage)
      setPendingScrollIndex(newIndex)
    } else {
      const orig = products[selectedIndex]
      const patch: Partial<InventoryImportProduct> = {}
      ;(
        [
          'productCode',
          'categoryId',
          'quantity',
          'declaredValue',
          'length',
          'width',
          'height',
          'weight',
        ] as Array<keyof InventoryImportProduct>
      ).forEach((k) => {
        if (editorDraft[k] !== (orig as any)[k])
          (patch as any)[k] = editorDraft[k]
      })
      if (Object.keys(patch).length > 0) onChangeProduct(selectedIndex, patch)
      const targetPage = Math.floor(selectedIndex / size)
      if (targetPage !== safePage) setPage(targetPage)
      setPendingScrollIndex(selectedIndex)
    }

    setSelectedIndex(null)
    setEditorDraft(null)
    setIsDirty(false)
  }

  // Cerrar con tecla ESC respetando confirmación si hay cambios sin guardar
  useEffect(() => {
    if (selectedIndex == null) return
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault()
        closeEditor()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // closeEditor is stable for our purposes; selectedIndex triggers attach/detach
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex])

  // Scroll a la tarjeta objetivo y resaltar en verde 2s
  useEffect(() => {
    if (pendingScrollIndex == null) return
    if (!displayIndices.includes(pendingScrollIndex)) return
    const el = cardRefs.current[pendingScrollIndex]
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      setHighlightIndex(pendingScrollIndex)
      setPendingScrollIndex(null)
      const t = setTimeout(() => setHighlightIndex(null), 2000)
      return () => clearTimeout(t)
    }
  }, [displayIndices, pendingScrollIndex])

  const pagesWindow = useMemo(() => {
    // Ventana deslizante: si hay más de 5 páginas, mostrar 4 números; si no, mostrar todas
    const windowSize = totalPages > 5 ? 4 : totalPages
    let startIdx = safePage
    if (startIdx + windowSize > totalPages)
      startIdx = Math.max(0, totalPages - windowSize)
    return Array.from({ length: windowSize }, (_, i) => startIdx + i)
  }, [safePage, totalPages])

  // Opciones de producto filtradas por pedido: quitar los códigos ya presentes en este pedido.
  // Si estamos editando un producto existente, permitimos su propio código para no bloquearlo.
  const filteredProductOptions = useMemo(() => {
    const existingCodes = new Set(
      (products || []).map((p) => (p.productCode || '').trim()).filter(Boolean)
    )
    // Si se está editando, permitir el código actual
    let allowedCode: string | null = null
    if (selectedIndex != null && selectedIndex >= 0) {
      const curr = products[selectedIndex]
      if (curr?.productCode) allowedCode = curr.productCode.trim()
    }
    return (productOptions || []).filter((opt: any) => {
      const code = (opt?.id || opt?.value || opt?.code || '').toString().trim()
      const available = (opt?.Count ?? 0) > 0
      if (!code) return false
      if (!available) return false
      if (allowedCode && code === allowedCode) return true
      return !existingCodes.has(code)
    })
  }, [productOptions, products, selectedIndex])

  const validateProduct = (
    product: InventoryImportProduct,
    products: InventoryImportProduct[],
    productsMap: Record<string, { Name: string; Count?: number }>,
    excludeIndex: number
  ): ProductErrors => {
    const errs: ProductErrors = {}
    const code = (product.productCode || '').trim()
    if (!code) {
      errs.productCode = 'Código requerido'
    } else {
      const duplicate = products.some(
        (p, idx) =>
          idx !== excludeIndex && (p.productCode || '').trim() === code
      )
      if (duplicate) errs.productCode = 'Producto duplicado'
    }
    if (!product.quantity || product.quantity <= 0) {
      errs.quantity = 'La cantidad debe ser mayor a 0'
    } else {
      const currentProduct = productsMap[code]
      if (currentProduct) {
        const prior = priorUsage?.[code] || 0
        const max = currentProduct.Count ?? 0
        const available = Math.max(0, max - prior)

        if (product.quantity > available) {
          errs.quantity = `Solo quedan ${available} disponibles`
          if (prior > 0) {
            errs.quantity += ` (usados en pedidos anteriores: ${prior})`
          }
        }
      }
    }
    if (product.categoryId == null) {
      errs.categoryId = 'Debe seleccionar una categoría'
    }
    if (product.declaredValue != null && product.declaredValue <= 0) {
      errs.declaredValue = 'El valor declarado debe ser mayor a 0'
    }
    if (product.length === 0) {
      errs.length = 'La medida largo no puede ser 0'
    }
    if (product.width === 0) {
      errs.width = 'La medida ancho no puede ser 0'
    }
    if (product.height === 0) {
      errs.height = 'La medida alto no puede ser 0'
    }
    if (product.weight === 0) {
      errs.weight = 'El peso no puede ser 0'
    }
    return errs
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Contenedor con borde para la lista de productos (incluye título, buscador, pagineos) */}
      <div className="border rounded p-3">
        {/* Encabezado: título + agregar */}
        <div className="flex items-center justify-between mb-3">
          <div className="row">
            <div className="text-sm font-semibold">Listado de Inventarios</div>
            {products.length === 0 && (
              <div className="text-xs text-red-600">
                Debe ingresar un inventario.
              </div>
            )}
          </div>
          {onAddProduct && (
            <PrimaryButton
              type="button"
              className="px-1 py-1 flex items-center gap-2 bg-main-500 hover:bg-main-600 text-white text-sm"
              onClick={() => {
                setSelectedIndex(-1)
                setEditorDraft({
                  productCode: '',
                  productName: '',
                  quantity: 0,
                  length: undefined,
                  width: undefined,
                  height: undefined,
                  weight: undefined,
                  declaredValue: undefined,
                  categoryId: undefined,
                })
                setIsDirty(false)
              }}
            >
              Agregar producto{' '}
              <PlusIcon className="h-4 w-4" aria-hidden="true" />
            </PrimaryButton>
          )}
        </div>

        {/* Controles: tamaño página + buscador */}
        <div className="flex items-end justify-between gap-3 mb-3">
          <FormSelect<number>
            id={`page-size-${orderIndex}`}
            name={`page-size-${orderIndex}`}
            label=""
            selected={size}
            options={pageSizes}
            optionString={(opt) => `${opt}/pág`}
            onSelectOption={(opt) => {
              setSize(opt)
              setPage(0)
            }}
            className="!h-8"
            containerClassname="w-40"
          />
          <div className="flex-1 max-w-xs">
            <FormText
              id={`prod-search-${orderIndex}`}
              name={`prod-search-${orderIndex}`}
              label=""
              placeholder="Buscar por Nombre, Codigo o Categoria"
              value={prodQuery}
              onChange={(e: any) => {
                setProdQuery(e.target.value)
                setPage(0)
              }}
              className="!h-8"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          {displayIndices.length === 0 ? (
            <div className="w-full text-center text-xs text-red-600">
              NO EXISTEN REGISTROS.
            </div>
          ) : (
            displayIndices.map((i) => {
              const p = products[i]
              const code = (p.productCode || '').trim()
              const prodInfo = code ? productsMap[code] : undefined
              const catName =
                pieceCategories.find(
                  (c) => c.id?.toString() === p.categoryId?.toString()
                )?.name || ''
              const selected = selectedIndex === i
              const e = errors?.[i] || {}
              const hasError = !!e && Object.values(e).some(Boolean)

              const prior = priorUsage?.[code] || 0
              const maxVal = prodInfo?.Count ?? 0
              const realAvailable = Math.max(0, maxVal - prior)

              return (
                <button
                  key={i}
                  type="button"
                  onClick={() =>
                    setSelectedIndex((curr) => (curr === i ? null : i))
                  }
                  ref={(el) => (cardRefs.current[i] = el)}
                  className={`group relative border rounded px-3 py-2 text-left w-60 min-h-[7.5rem] overflow-hidden font-semibold ${
                    hasError
                      ? 'border-red-500 bg-red-50'
                      : highlightIndex === i
                        ? 'border-green-500'
                        : selected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200'
                  }`}
                  title={
                    hasError
                      ? Object.entries(e)
                          .filter(([_, value]) => value)
                          .map(([key, value]) => `${key}: ${value}`)
                          .join('\n')
                      : (code || '—') +
                        (prodInfo?.Name ? ` · ${prodInfo.Name}` : '')
                  }
                >
                  <div className="text-sm truncate max-w-[13rem]">
                    {(code || '—') +
                      (prodInfo?.Name ? ` · ${prodInfo.Name}` : '')}
                  </div>
                  <div className="text-xs mt-1 space-y-0.5">
                    <div className="truncate">
                      Cantidad: {p.quantity || 0}
                      {prodInfo?.Count != null
                        ? ` · Disponible: ${realAvailable}`
                        : ''}
                    </div>
                    <div className="truncate">
                      Dimensiones (L×A×H): {p.length || 0}×{p.width || 0}×
                      {p.height || 0}
                    </div>
                    <div className="truncate">Peso: {p.weight || 0}</div>
                    <div className="truncate">
                      Valor declarado: {p.declaredValue || 0}
                    </div>
                    <div className="truncate">Categoría: {catName || '—'}</div>
                  </div>
                  {onRemoveProduct && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-label="Eliminar"
                      className="absolute text-red-500 hover:text-red-700  top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-100 cursor-pointer"
                      title="Eliminar"
                      onClick={(e) => {
                        e.stopPropagation()
                        onRemoveProduct(i)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          onRemoveProduct(i)
                        }
                      }}
                    >
                      <TrashIcon className="w-5 h-5 text-red-500 hover:text-red-700 " />
                    </span>
                  )}
                </button>
              )
            })
          )}
        </div>

        {/* Paginación al final, lado derecho (dentro del recuadro) */}
        <div className="mt-2 flex items-center justify-end gap-2">
          <PaginationNavButton
            direction="prev"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
          />
          {pagesWindow.map((p) => (
            <PaginationPageButton
              key={p}
              page={p + 1}
              active={p === safePage}
              onClick={() => setPage(p)}
            />
          ))}
          <PaginationNavButton
            direction="next"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1}
          />
        </div>
      </div>

      {/* Editor en modal */}
      {selectedIndex != null &&
        (selectedIndex === -1 || products[selectedIndex]) &&
        (() => {
          const index = selectedIndex
          const row =
            editorDraft ||
            (index != null && index >= 0
              ? products[index]
              : ({} as InventoryImportProduct))
          const rowErrors = validateProduct(
            row,
            products,
            productsMap,
            index === -1 ? products.length : index
          )
          const code = (row.productCode || '').trim()
          const currentProduct = code ? productsMap[code] : undefined

          const prior = priorUsage?.[code] || 0
          const maxVal = currentProduct?.Count ?? 0
          const realAvailable = Math.max(0, maxVal - prior)

          return ReactDOM.createPortal(
            <div
              className="fixed inset-0 z-[999] flex items-center justify-center bg-black/30"
              onMouseDown={closeEditor}
            >
              <div
                className="bg-white w-[760px] max-w-[95vw] rounded shadow-lg p-4"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-base font-semibold">
                    {index === -1 ? 'Agregar producto' : 'Editar producto'}
                  </h4>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100"
                    title="Cerrar"
                    onClick={closeEditor}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <FormSearch
                      id={`prodCode-${orderIndex}-${index}`}
                      name={`prodCode-${orderIndex}-${index}`}
                      label={`Producto`}
                      placeholder="Busca por código o nombre"
                      value={productSearchValue}
                      options={filteredProductOptions || []}
                      error={rowErrors.productCode}
                      className="!h-8"
                      onChange={(evt: any) =>
                        setProductSearchValue(evt.target.value)
                      }
                      onSelectOption={(opt: any) => {
                        const code = opt?.id || opt?.value || opt?.code || ''
                        const codeStr = (code || '').toString()
                        const info = codeStr
                          ? (productsMap as any)[codeStr]
                          : undefined
                        // Autocompletar desde catálogo si existe
                        updateDraft({
                          productCode: codeStr,
                          productName: info?.Name,
                          length: info?.Length,
                          width: info?.Width,
                          height: info?.Height,
                          weight: info?.Weight,
                          declaredValue: info?.DeclareValues,
                          quantity:
                            row.quantity && row.quantity > 0 ? row.quantity : 1,
                        })
                        // Update search value to display
                        const display = info
                          ? `${codeStr} · ${info.Name}`
                          : codeStr
                        setProductSearchValue(display)
                      }}
                      onFilter={(opt: any, filter: string) => {
                        const tokens = filter?.toLowerCase().split(' ')
                        return tokens?.every((token) => {
                          return (
                            token === '-' ||
                            opt.name?.toLowerCase().includes(token) ||
                            opt.code?.toLowerCase().includes(token) ||
                            opt.id?.toString().toLowerCase().includes(token)
                          )
                        })
                      }}
                      isTabled={true}
                      RenderOption={({ option }: any) => {
                        const optCode = (
                          option?.id ||
                          option?.value ||
                          option?.code ||
                          ''
                        ).toString()
                        const optPrior = priorUsage?.[optCode] || 0
                        const optMax = option?.Count ?? 0
                        const optAvailable = Math.max(0, optMax - optPrior)
                        return (
                          <div className="px-2 py-1 text-sm">
                            <div>{option?.name || `${option?.code}`}</div>
                            {option?.Count != null && (
                              <div className="text-[11px] text-gray-500">
                                Disp: {optAvailable}
                              </div>
                            )}
                          </div>
                        )
                      }}
                    />
                  </div>

                  <FormSearch
                    id={`cat-${orderIndex}-${index}`}
                    name={`cat-${orderIndex}-${index}`}
                    label="Categoría"
                    placeholder="Selecciona categoría"
                    value={categorySearchValue}
                    options={pieceCategories}
                    error={rowErrors.categoryId}
                    className="!h-8"
                    onChange={(evt: any) =>
                      setCategorySearchValue(evt.target.value)
                    }
                    onSelectOption={(opt: any) => {
                      updateDraft({ categoryId: opt?.id })
                      setCategorySearchValue(
                        opt?.name || opt?.description || ''
                      )
                    }}
                    onFilter={(opt: any, filter: string) => {
                      const tokens = filter?.toLowerCase().split(' ')
                      return tokens?.every((token) => {
                        return (
                          token === '-' ||
                          opt.name?.toLowerCase().includes(token) ||
                          opt.description?.toLowerCase().includes(token)
                        )
                      })
                    }}
                    isTabled={true}
                    RenderOption={({ option }: any) => (
                      <div className="px-2 py-1 text-sm">
                        {option.name || option.description}
                      </div>
                    )}
                  />

                  <div className="flex flex-col">
                    <FormText
                      id={`qty-${orderIndex}-${index}`}
                      name={`qty-${orderIndex}-${index}`}
                      label={
                        currentProduct
                          ? `Cantidad (Disp: ${realAvailable})`
                          : 'Cantidad'
                      }
                      value={(row.quantity ?? 0).toString()}
                      error={rowErrors.quantity}
                      onChange={(evt: any) => {
                        const val = (evt.target.value || '').toString()
                        if (/^\d*$/.test(val)) {
                          updateDraft({ quantity: Number(val || 0) })
                        }
                      }}
                      className="!h-8"
                    />
                  </div>

                  <FormText
                    id={`decl-${orderIndex}-${index}`}
                    name={`decl-${orderIndex}-${index}`}
                    label="Valor declarado"
                    value={(row.declaredValue ?? '').toString()}
                    error={rowErrors.declaredValue}
                    onChange={(evt: any) =>
                      updateDraft({
                        declaredValue: Number(evt.target.value || 0),
                      })
                    }
                    className="!h-8"
                    disabled
                  />

                  <FormText
                    id={`len-${orderIndex}-${index}`}
                    name={`len-${orderIndex}-${index}`}
                    label="Largo"
                    value={row.length?.toString() || ''}
                    onChange={(evt: any) =>
                      updateDraft({ length: Number(evt.target.value || 0) })
                    }
                    disabled
                    className="!h-8"
                    error={rowErrors.length}
                  />
                  <FormText
                    id={`wid-${orderIndex}-${index}`}
                    name={`wid-${orderIndex}-${index}`}
                    label="Ancho"
                    value={row.width?.toString() || ''}
                    onChange={(evt: any) =>
                      updateDraft({ width: Number(evt.target.value || 0) })
                    }
                    disabled
                    className="!h-8"
                    error={rowErrors.width}
                  />
                  <FormText
                    id={`hei-${orderIndex}-${index}`}
                    name={`hei-${orderIndex}-${index}`}
                    label="Alto"
                    value={row.height?.toString() || ''}
                    onChange={(evt: any) =>
                      updateDraft({ height: Number(evt.target.value || 0) })
                    }
                    disabled
                    className="!h-8"
                    error={rowErrors.height}
                  />
                  <FormText
                    id={`wei-${orderIndex}-${index}`}
                    name={`wei-${orderIndex}-${index}`}
                    label="Peso"
                    value={row.weight?.toString() || ''}
                    onChange={(evt: any) =>
                      updateDraft({ weight: Number(evt.target.value || 0) })
                    }
                    disabled
                    className="!h-8"
                    error={rowErrors.weight}
                  />
                </div>
                <div className="mt-4 flex justify-end">
                  <PrimaryButton
                    type="button"
                    className="px-3 py-2 rounded bg-green-600 text-white text-sm disabled:opacity-50"
                    onClick={saveChanges}
                    disabled={Object.keys(rowErrors).length > 0}
                  >
                    Guardar producto
                  </PrimaryButton>
                </div>
              </div>
            </div>,
            document.body
          )
        })()}

      {/* Modal de confirmación de cierre */}
      <InventoryImportConfirmCloseModal
        show={showConfirmClose}
        onCancel={() => confirmClose(false)}
        onConfirm={() => confirmClose(true)}
      />
    </div>
  )
}
